import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { createDkimKey } from 'src/actions/dkimKeys';
import { showAlert } from 'src/actions/globalAlert';
import { ExternalLink, PageLink, SubduedLink } from 'src/components/links';
// prettier-ignore
import { Box, Button, Layout, Page, Panel, Stack, Text, TextField, Toggle } from 'src/components/matchbox';
import { SubduedText } from 'src/components/text';
import { Form } from 'src/components/tracking';
import { selectDomain } from 'src/selectors/sendingDomains';
import { DETAILS_BASE_URL } from '../constants';
import Domains from './index';

const DKIM_DOCS_URL =
  'https://developers.sparkpost.com/api/sending-domains/#sending-domains-dkim-keys';

function CreateDkimKeyPage({ domain, createDkimKey, showAlert, createLoading }) {
  const history = useHistory();
  const { id: domainId } = useParams();
  const subaccountId = domain?.subaccount_id;
  const [bringYourOwnKeys, setBringYourOwnKeys] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      id: '',
      signing_domain: '',
      headers: 'from:to:subject:date',
      selector: '',
      private: '',
      public: ''
    }
  });

  const onSubmit = (data) => {
    const payload = {
      domain: domainId,
      subaccount: subaccountId,
      id: data.id
    };

    if (data.signing_domain) {
      payload.signing_domain = data.signing_domain;
    }

    if (data.headers) {
      payload.headers = data.headers;
    }

    if (bringYourOwnKeys) {
      if (data.selector) {
        payload.selector = data.selector;
      }
      if (data.private) {
        payload.private = data.private;
      }
      if (data.public) {
        payload.public = data.public;
      }
    }

    return createDkimKey(payload)
      .then(() => {
        showAlert({
          type: 'success',
          message: `DKIM key "${data.id}" created successfully.`
        });
        history.push(`${DETAILS_BASE_URL}/sending-bounce/${domainId}/dkim-keys/${data.id}`);
      })
      .catch((err) => {
        showAlert({
          type: 'error',
          message: 'Failed to create DKIM key',
          details: err.message
        });
      });
  };

  const dkimKeysPath = `${DETAILS_BASE_URL}/sending-bounce/${domainId}/dkim-keys`;

  return (
    <Domains.Container>
      <Page
        title="Add DKIM Key"
        breadcrumbAction={{
          content: domainId,
          as: PageLink,
          to: dkimKeysPath
        }}
      >
        <Form onSubmit={handleSubmit(onSubmit)} id="create-dkim-key-form">
          <Layout>
            <Layout.Section annotated>
              <Layout.SectionTitle>Key Details</Layout.SectionTitle>

              <Stack>
                <SubduedText fontSize="200">
                  Create a new DKIM signing key for this domain. After creation, you will need to
                  add the DNS record and verify the key before it can be used for signing.
                </SubduedText>

                <SubduedLink as={ExternalLink} to={DKIM_DOCS_URL} fontSize="200">
                  DKIM Keys Documentation
                </SubduedLink>
              </Stack>
            </Layout.Section>

            <Layout.Section>
              <Panel>
                <Panel.Section>
                  <Stack>
                    <TextField
                      id="dkim-key-id"
                      label="Key ID"
                      helpText="A unique identifier for this key (e.g., 'marketing', 'transactional'). Use only letters, numbers, hyphens, and underscores."
                      placeholder="e.g., marketing"
                      disabled={createLoading}
                      error={errors.id?.message}
                      {...register('id', {
                        required: 'Key ID is required',
                        pattern: {
                          value: /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/,
                          message:
                            'Key ID must start with a letter or number and contain only letters, numbers, hyphens, and underscores (max 64 characters)'
                        }
                      })}
                    />

                    <TextField
                      id="dkim-signing-domain"
                      label="Signing Domain"
                      helpText="Optional. The domain to use in the DKIM signature. Defaults to the sending domain if not specified."
                      placeholder={domainId}
                      disabled={createLoading}
                      error={errors.signing_domain?.message}
                      {...register('signing_domain')}
                    />

                    <TextField
                      id="dkim-headers"
                      label="Headers"
                      helpText="Colon-separated list of headers to include in the DKIM signature."
                      disabled={createLoading}
                      error={errors.headers?.message}
                      {...register('headers')}
                    />
                  </Stack>
                </Panel.Section>

                <Panel.Section>
                  <Stack>
                    <Box>
                      <Text as="h3" fontSize="300" fontWeight="semibold" mb="200">
                        Key Generation
                      </Text>
                      <Text color="gray.700" fontSize="200" mb="400">
                        By default, we will generate a secure DKIM keypair for you. Enable the
                        option below if you want to provide your own keys.
                      </Text>
                    </Box>

                    <Toggle
                      id="bring-your-own-keys"
                      label="Bring Your Own Keys"
                      helpText="Provide your own DKIM keypair instead of having one generated automatically."
                      disabled={createLoading}
                      checked={bringYourOwnKeys}
                      onChange={() => setBringYourOwnKeys(!bringYourOwnKeys)}
                    />

                    {bringYourOwnKeys && (
                      <>
                        <TextField
                          id="dkim-selector"
                          label="Selector"
                          helpText="The DKIM selector for DNS lookup (e.g., 'my-selector-2025')."
                          placeholder="e.g., my-selector-2025"
                          disabled={createLoading}
                          error={errors.selector?.message}
                          {...register('selector', {
                            required: bringYourOwnKeys
                              ? 'Selector is required when providing your own keys'
                              : false
                          })}
                        />

                        <TextField
                          id="dkim-private-key"
                          label="Private Key"
                          helpText="Base64-encoded RSA private key."
                          placeholder="MIICXgIBAAKBgQC+W6scd3XWwvC..."
                          disabled={createLoading}
                          multiline
                          rows={4}
                          error={errors.private?.message}
                          {...register('private', {
                            required: bringYourOwnKeys
                              ? 'Private key is required when providing your own keys'
                              : false
                          })}
                        />

                        <TextField
                          id="dkim-public-key"
                          label="Public Key"
                          helpText="Base64-encoded RSA public key."
                          placeholder="MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQC+W6scd3XWwvC..."
                          disabled={createLoading}
                          multiline
                          rows={4}
                          error={errors.public?.message}
                          {...register('public', {
                            required: bringYourOwnKeys
                              ? 'Public key is required when providing your own keys'
                              : false
                          })}
                        />
                      </>
                    )}
                  </Stack>
                </Panel.Section>
              </Panel>
            </Layout.Section>
          </Layout>

          <Layout>
            <Layout.Section annotated />
            <Layout.Section>
              <Button variant="primary" type="submit" loading={createLoading}>
                Add DKIM Key
              </Button>
            </Layout.Section>
          </Layout>
        </Form>
      </Page>
    </Domains.Container>
  );
}

const mapStateToProps = (state) => ({
  domain: selectDomain(state),
  createLoading: state.dkimKeys.createLoading
});

export default connect(mapStateToProps, {
  createDkimKey,
  showAlert
})(CreateDkimKeyPage);
